import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash, FloppyDisk, PencilSimple } from "@phosphor-icons/react";
import api, { formatApiErrorDetail } from "../../lib/api";
import { formatTrialDuration } from "../../lib/trialDuration";

const blankAddon = { slug: "", name: "", monthly: 99, yearly: 990, default_in_basic: false };

export default function PricingPanel() {
  const [cfg, setCfg] = useState(null);
  const [draft, setDraft] = useState(null);
  const [savingPlans, setSavingPlans] = useState(false);
  const [editingAddon, setEditingAddon] = useState(null); // slug or "new"
  const [addonDraft, setAddonDraft] = useState(blankAddon);

  const load = async () => {
    try {
      const { data } = await api.get("/admin/pricing");
      setCfg(data);
      setDraft({
        basic: { monthly: data.basic.monthly, yearly: data.basic.yearly },
        pro: { monthly: data.pro.monthly, yearly: data.pro.yearly },
        trial_days: data.trial_days || 180,
      });
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  useEffect(() => { load(); }, []);

  const savePlans = async () => {
    setSavingPlans(true);
    try {
      await api.put("/admin/pricing", draft);
      toast.success("Pricing updated.");
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally { setSavingPlans(false); }
  };

  const startNewAddon = () => {
    setAddonDraft(blankAddon);
    setEditingAddon("new");
  };

  const startEditAddon = (a) => {
    setAddonDraft({ ...a, sub_features: a.sub_features || [] });
    setEditingAddon(a.slug);
  };

  const saveAddon = async () => {
    try {
      if (editingAddon === "new") {
        await api.post("/admin/pricing/addons", addonDraft);
      } else {
        await api.put(`/admin/pricing/addons/${editingAddon}`, addonDraft);
      }
      toast.success("Add-on saved.");
      setEditingAddon(null);
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const deleteAddon = async (slug) => {
    if (!window.confirm(`Remove add-on "${slug}"? Users who already paid for it will keep access until expiry.`)) return;
    try {
      await api.delete(`/admin/pricing/addons/${slug}`);
      toast.success("Removed.");
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  if (!cfg || !draft) return <div className="text-muted-foreground" data-testid="admin-pricing-loading">Loading…</div>;

  return (
    <div className="space-y-8" data-testid="admin-pricing-panel">
      {/* Plan prices */}
      <div className="card-flat p-6">
        <h3 className="font-serif text-2xl mb-4">Plan pricing (₹)</h3>
        <div className="grid sm:grid-cols-2 gap-6">
          <PlanEditor
            title="Basic"
            value={draft.basic}
            onChange={(v) => setDraft({ ...draft, basic: v })}
            testidPrefix="admin-price-basic"
          />
          <PlanEditor
            title="Pro"
            value={draft.pro}
            onChange={(v) => setDraft({ ...draft, pro: v })}
            testidPrefix="admin-price-pro"
          />
        </div>
        <div className="flex flex-wrap items-end gap-4 mt-6">
          <label className="text-sm">
            <span className="block text-xs text-muted-foreground mb-1">Trial duration (days) · applies to NEW signups only</span>
            <input
              type="number"
              min={0} max={730}
              value={draft.trial_days}
              onChange={(e) => setDraft({ ...draft, trial_days: parseInt(e.target.value || "0") })}
              className="w-28 bg-background border hairline px-3 py-2 text-sm"
              data-testid="admin-price-trial-days"
            />
            <span
              className="block text-[11px] text-muted-foreground mt-1 max-w-md"
              data-testid="admin-trial-duration-hint"
            >
              Currently: <span className="text-foreground">{formatTrialDuration(draft.trial_days)}</span> with Basic functionality.
              Existing users keep their current trial end date — this change never shortens an active trial.
            </span>
          </label>
          <button
            onClick={savePlans}
            disabled={savingPlans}
            className="btn-primary px-4 py-2 text-sm inline-flex items-center gap-2 disabled:opacity-60"
            data-testid="admin-price-save"
          >
            <FloppyDisk size={14} weight="duotone" /> {savingPlans ? "Saving…" : "Save plan prices"}
          </button>
        </div>
      </div>

      {/* Add-ons */}
      <div className="card-flat p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-serif text-2xl">Basic add-ons</h3>
          <button
            onClick={startNewAddon}
            className="btn-ghost px-3 py-2 text-xs inline-flex items-center gap-2"
            data-testid="admin-addon-add"
          >
            <Plus size={12} weight="bold" /> Add add-on
          </button>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-muted-foreground border-b hairline">
                <th className="py-2 pr-3">Slug</th>
                <th className="py-2 pr-3">Name</th>
                <th className="py-2 pr-3">Monthly</th>
                <th className="py-2 pr-3">Yearly</th>
                <th className="py-2 pr-3">Default in Basic</th>
                <th className="py-2 pr-3"></th>
              </tr>
            </thead>
            <tbody>
              {cfg.addons.map((a) => (
                <tr key={a.slug} className={`border-b hairline ${a.default_in_basic ? "bg-[hsl(var(--muted))]/40" : ""}`} data-testid={`admin-addon-row-${a.slug}`}>
                  <td className="py-2 pr-3 font-mono text-xs">
                    {a.slug}
                    {(a.sub_features || []).length > 0 && (
                      <span className="ml-1 text-[10px] text-muted-foreground">·{a.sub_features.length} subs</span>
                    )}
                  </td>
                  <td className="py-2 pr-3">{a.name}</td>
                  <td className="py-2 pr-3">{a.default_in_basic ? <span className="text-[hsl(var(--secondary))]">₹0 (Free)</span> : `₹${a.monthly}`}</td>
                  <td className="py-2 pr-3">{a.default_in_basic ? <span className="text-[hsl(var(--secondary))]">₹0 (Free)</span> : `₹${a.yearly}`}</td>
                  <td className="py-2 pr-3">{a.default_in_basic ? <span className="text-[hsl(var(--secondary))]">Yes (free in Basic)</span> : "No"}</td>
                  <td className="py-2 pr-3 text-right">
                    <button onClick={() => startEditAddon(a)} className="text-xs text-muted-foreground hover:text-foreground mr-3" data-testid={`admin-addon-edit-${a.slug}`}>
                      <PencilSimple size={14} weight="duotone" className="inline" /> Edit
                    </button>
                    <button onClick={() => deleteAddon(a.slug)} className="text-xs text-[hsl(var(--primary))] hover:text-foreground" data-testid={`admin-addon-delete-${a.slug}`}>
                      <Trash size={14} weight="duotone" className="inline" /> Remove
                    </button>
                  </td>
                </tr>
              ))}
              {cfg.addons.length === 0 && (
                <tr><td colSpan={6} className="py-6 text-center text-muted-foreground">No add-ons. Click "Add add-on".</td></tr>
              )}
            </tbody>
          </table>
        </div>

        {editingAddon && (
          <div className="border hairline mt-6 p-4" data-testid="admin-addon-editor">
            <div className="eyebrow mb-3">{editingAddon === "new" ? "New add-on" : `Edit · ${editingAddon}`}</div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-3">
              <Field label="Slug" disabled={editingAddon !== "new"} value={addonDraft.slug} onChange={(v) => setAddonDraft({ ...addonDraft, slug: v })} testid="admin-addon-slug" />
              <Field label="Name" value={addonDraft.name} onChange={(v) => setAddonDraft({ ...addonDraft, name: v })} testid="admin-addon-name" />
              <Field label="Monthly ₹" type="number" value={addonDraft.monthly} onChange={(v) => setAddonDraft({ ...addonDraft, monthly: parseInt(v || "0") })} testid="admin-addon-monthly" />
              <Field label="Yearly ₹" type="number" value={addonDraft.yearly} onChange={(v) => setAddonDraft({ ...addonDraft, yearly: parseInt(v || "0") })} testid="admin-addon-yearly" />
              <label className="text-sm flex items-end gap-2">
                <input type="checkbox" checked={addonDraft.default_in_basic} onChange={(e) => setAddonDraft({ ...addonDraft, default_in_basic: e.target.checked })} data-testid="admin-addon-default" />
                <span className="text-xs">Default in Basic</span>
              </label>
            </div>
            <div className="flex justify-end gap-2 mt-4">
              <button onClick={() => setEditingAddon(null)} className="btn-ghost px-3 py-2 text-xs" data-testid="admin-addon-cancel">Cancel</button>
              <button onClick={saveAddon} className="btn-primary px-3 py-2 text-xs inline-flex items-center gap-1" data-testid="admin-addon-save">
                <FloppyDisk size={12} weight="duotone" /> Save add-on
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function PlanEditor({ title, value, onChange, testidPrefix }) {
  return (
    <div>
      <div className="font-serif text-lg mb-2">{title}</div>
      <div className="grid grid-cols-2 gap-3">
        <Field label="Monthly ₹" type="number" value={value.monthly} onChange={(v) => onChange({ ...value, monthly: parseInt(v || "0") })} testid={`${testidPrefix}-monthly`} />
        <Field label="Yearly ₹" type="number" value={value.yearly} onChange={(v) => onChange({ ...value, yearly: parseInt(v || "0") })} testid={`${testidPrefix}-yearly`} />
      </div>
    </div>
  );
}

function Field({ label, value, onChange, type = "text", disabled, testid }) {
  return (
    <label className="text-sm">
      <span className="block text-xs text-muted-foreground mb-1">{label}</span>
      <input
        type={type}
        value={value}
        disabled={disabled}
        onChange={(e) => onChange(e.target.value)}
        className="w-full bg-background border hairline px-3 py-2 text-sm disabled:opacity-60"
        data-testid={testid}
      />
    </label>
  );
}
